import { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { Button } from '@/atoms/Button'
import { CircleInfoIcon } from '@/atoms/Icons/CircleInfoIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { ExportIcon } from '@/atoms/Icons/ExportIcon'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { ParagraphSM } from '@/atoms/Text'
import { ReactFCC } from '@/types/react'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { useMultipartUpload } from '../../components/useMultipartUpload'
import { formatSize } from './uploadHelpers'

interface UploadBoxProps {
  isActive?: boolean
  uploadedFileNames?: string[]
  acknowledged?: boolean
  maxSize?: number
  acceptedFileType: 'image' | 'video'
  loadFiles: () => void
}

export const UploadBox: ReactFCC<UploadBoxProps> = ({
  acknowledged,
  maxSize,
  acceptedFileType,
  loadFiles,
  uploadedFileNames,
  isActive = true,
}) => {
  const { t } = useTranslate('guild-create')
  const { query } = useRouter()
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<{ type: string; message: string } | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [file, setFile] = useState<File | null | undefined>(null)
  const [view, setView] = useState<'acknowledge' | 'uploading' | 'error' | 'upload'>('uploading')
  const [uploadMultipart, abortUpload, uploadProgress] = useMultipartUpload({
    id: query.id as string,
    type: 'APPLICATION',
  })

  useEffect(() => {
    if (!acknowledged) {
      setView('acknowledge')
      return
    }

    if (!file) {
      setView('upload')
      if (inputRef.current) inputRef.current.value = ''
      return
    }
  }, [acknowledged, file])

  useEffect(() => {
    if (!file) return

    if (maxSize && file.size > maxSize) {
      setError({
        type: t('fileTooLarge', 'File too large'),
        message: t('fileMustBeLessThan', 'File must be less than {{ maxSize }}', {
          maxSize: formatSize(maxSize),
        }),
      })
      setView('error')
      return
    }

    if (acceptedFileType === 'image' && !file.type.startsWith('image')) {
      setError({
        type: t('invalidFileFormat', 'Invalid file format'),
        message: t('supportedFormatsImageOnly', 'Supported File Formats: Any image file type (Image Files Only)'),
      })
      setView('error')
      return
    }

    if (acceptedFileType === 'video' && !file.type.startsWith('video')) {
      setError({
        type: t('invalidFileFormat', 'Invalid file format'),
        message: t('supportedFormatsVideoOnly', 'Supported File Formats: Any video file type (Video Files Only)'),
      })
      setView('error')
      return
    }

    if (uploadedFileNames?.includes(file.name)) {
      setError({
        type: t('invalidFileName', 'Invalid file name'),
        message: t('thisFileHasAlreadyBeenUploaded', 'This file has already been uploaded'),
      })
      setView('error')
      return
    }

    setError(null)
    setView('uploading')

    uploadMultipart(file)
      .then(() => {
        loadFiles()
      })
      .catch((error) => {
        setError({
          type: t('uploadFailed', 'Upload failed'),
          message: error.message || t('unknownError', 'Unknown error'),
        })
        setView('error')
      })
  }, [acceptedFileType, file, maxSize, t, loadFiles, uploadMultipart, uploadedFileNames])

  const isUpload = view === 'upload'
  const isError = view === 'error'
  const isAcknowledge = view === 'acknowledge'
  const isUploading = view === 'uploading'

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    setFile(file)
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)

    if (isUploading || isAcknowledge) return

    if (event.dataTransfer?.files?.length) {
      setFile(event.dataTransfer.files.item(0))
    } else if (event.dataTransfer.items?.length) {
      setFile(event.dataTransfer.items[0].getAsFile())
    }
  }

  const handleClick = () => {
    if (isUploading || isAcknowledge) return

    setError(null)
    setFile(null)
  }

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(true)
  }

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)
  }

  const handleAbort = () => {
    abortUpload()
    setFile(null)
    setView('upload')
  }

  return (
    <div
      className={classNames(
        'flex rounded-lg p-4 border-core-gray-300 items-center h-60 border-2 border-dashed justify-center',
        isDragging && isUpload && 'border-info-500 bg-info-50',
        isDragging && !isUpload && 'border-error-500 bg-error-100',
        isError && 'border-error-500 bg-error-100',
        isUpload ? 'cursor-pointer' : 'cursor-not-allowed',
        isUploading && 'bg-core-gray-100',
      )}
      onClick={isActive ? handleClick : undefined}
      onDragEnter={isActive ? handleDragEnter : undefined}
      onDragOver={isActive ? handleDragOver : undefined}
      onDragLeave={isActive ? handleDragLeave : undefined}
      onDrop={isActive ? handleDrop : undefined}
    >
      <input type="file" className="hidden" ref={inputRef} onChange={handleInputChange} />

      {!isActive && (
        <div className="flex select-none flex-col items-center gap-1">
          <CircleInfoIcon size={48} color="info-500" />
          <ParagraphSM>{t('toUploadPleaseUploadVideoAbove', 'To upload, please upload video above.')}</ParagraphSM>
        </div>
      )}

      {isAcknowledge && (
        <div className="flex select-none flex-col items-center gap-1">
          <CircleInfoIcon size={48} color="info-500" />
          <ParagraphSM>
            {t('toUploadPleaseAcknowledge', 'To upload, please acknowledge the content restrictions above.')}
          </ParagraphSM>
        </div>
      )}

      {isError && (
        <div className="flex select-none flex-col items-center gap-1">
          <CloseIcon size={48} color="error-500" />
          <ParagraphSM>{error?.type}</ParagraphSM>
          <ParagraphSM color="core-gray-600">{error?.message}</ParagraphSM>
          <Button className="mt-4" variant="black" onClick={() => setError(null)}>
            {t('dismiss', 'Dismiss')}
          </Button>
        </div>
      )}

      {isUpload && isActive && (
        <div className="flex select-none flex-col items-center gap-1">
          <ExportIcon size={48} color="core-gray-950" />
          <ParagraphSM>
            <Translate t={t} i18nKey="dragDropFileHereFile" defaults="Drag and Drop Your File Here, or " />
            <span
              className="border-oxide font-bold text-oxide hover:border-b-2"
              onClick={() => inputRef?.current?.click()}
            >
              {t('browseFiles', 'Browse Files')}
            </span>
          </ParagraphSM>

          {maxSize && (
            <ParagraphSM color="core-gray-600">
              {t('maxSize', 'Max Size: {{ maxSize }}', { maxSize: formatSize(maxSize) })}
            </ParagraphSM>
          )}
        </div>
      )}

      {isUploading && (
        <div className="flex select-none flex-col items-center gap-1">
          <LoadingSpinnerIcon size={48} color="core-gray-950" />
          <ParagraphSM className="flex justify-between">
            {t('uploading', 'Uploading...')}
            <span className="w-10 pl-1 text-right">{uploadProgress}%</span>
          </ParagraphSM>
          <ParagraphSM color="core-gray-600">
            {t('thisMayTakeAFewMinutes', "This may take a few minutes... please don't leave the page")}
          </ParagraphSM>
          <Button variant="gray" onClick={handleAbort}>
            {t('cancel', 'Cancel')}
          </Button>
        </div>
      )}
    </div>
  )
}
